import React, { useState } from 'react';
import { 
  BuildingOfficeIcon, 
  LockClosedIcon, 
  EyeIcon, 
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const SSOLogin = () => {
  const [loginMethod, setLoginMethod] = useState('sso'); // 'sso' or 'credentials'
  const [ssoProvider, setSsoProvider] = useState('okta');
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const ssoProviders = [
    {
      id: 'okta',
      name: 'Okta',
      logo: '🔐',
      description: 'Enterprise identity platform'
    },
    {
      id: 'azure',
      name: 'Azure AD',
      logo: '☁️',
      description: 'Microsoft identity service'
    },
    {
      id: 'google',
      name: 'Google Workspace',
      logo: '🔍',
      description: 'Google enterprise suite'
    },
    {
      id: 'saml',
      name: 'SAML 2.0',
      logo: '🔗',
      description: 'Custom SAML provider'
    },
    {
      id: 'onelogin',
      name: 'OneLogin',
      logo: '🔑',
      description: 'Unified access management'
    }
  ];

  const handleSSOLogin = async (provider) => {
    setIsLoading(true);
    setError('');
    
    try {
      // TODO: Implement actual SSO flow
      // This would typically redirect to the SSO provider
      console.log(`Initiating SSO login with ${provider}`);
      
      // Simulate SSO redirect
      setTimeout(() => {
        setIsLoading(false);
        // In real implementation, this would redirect to SSO provider
        window.location.href = `/auth/sso/${provider}`;
      }, 1000);
      
    } catch (error) {
      setError('SSO login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // TODO: Implement actual credential login
      console.log('Credential login:', credentials);
      
      // Simulate API call
      setTimeout(() => {
        setIsLoading(false);
        // In real implementation, this would validate credentials
        if (credentials.email && credentials.password) {
          // Redirect to appropriate dashboard based on user role
          window.location.href = '/dashboard';
        } else {
          setError('Please enter valid credentials.');
        }
      }, 1000);
      
    } catch (error) {
      setError('Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <BuildingOfficeIcon className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Enterprise Persona
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          AI Compliance Monitoring Platform
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Login Method Toggle */}
          <div className="flex rounded-md shadow-sm mb-6">
            <button
              type="button"
              onClick={() => setLoginMethod('sso')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-md border ${
                loginMethod === 'sso'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
          🔐 SSO Login
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('credentials')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-md border-t border-r border-b ${
                loginMethod === 'credentials'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
          📧 Credentials
            </button>
          </div>

          {loginMethod === 'sso' ? (
            /* SSO Login */
            <div>
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Choose Your SSO Provider
                </h3>
                <p className="text-sm text-gray-600">
                  Select your enterprise identity provider to continue
                </p>
              </div>

              <div className="space-y-3">
                {ssoProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleSSOLogin(provider.id)}
                    disabled={isLoading}
                    className="w-full flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex-shrink-0">
                      <span className="text-2xl">{provider.logo}</span>
                    </div>
                    <div className="ml-4 text-left">
                      <div className="text-sm font-medium text-gray-900">
                        {provider.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {provider.description}
                      </div>
                    </div>
                    <div className="ml-auto">
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      ) : (
                        <div className="w-5 h-5 text-gray-400">
                          →
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Need help? Contact your IT administrator
                </p>
              </div>
            </div>
          ) : (
            /* Credential Login */
            <form onSubmit={handleCredentialLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Enterprise Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={credentials.email}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="john.doe@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={credentials.password}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                    Forgot your password?
                  </a>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {error}
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <LockClosedIcon className="w-5 h-5 mr-2" />
                      Sign in
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to our{' '}
              <a href="/terms" className="text-blue-600 hover:text-blue-500">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-blue-600 hover:text-blue-500">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSOLogin;
